import React, { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Portaled modal — always renders to document.body so no ancestor stacking
 * context (transforms, backdrop-filter, filter, perspective, will-change) can
 * trap it behind sibling cards. Comes with ESC-to-close, body-scroll lock,
 * backdrop-click to dismiss, and a11y attrs.
 */
export default function Modal({ open, onClose, children, size = "xl", testId }) {
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose && onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const sizeClass = {
    sm: "max-w-sm", md: "max-w-md", lg: "max-w-lg",
    xl: "max-w-xl", "2xl": "max-w-2xl", "3xl": "max-w-3xl",
  }[size] || "max-w-xl";

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/60"
      style={{ zIndex: 2147483000 }}
      data-testid={testId}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose && onClose(); }}
    >
      <div className={`pc-card p-6 w-full ${sizeClass} max-h-[90vh] overflow-y-auto`} role="dialog" aria-modal="true">
        {children}
      </div>
    </div>,
    document.body
  );
}

export function ModalHeader({ icon: Icon, title, onClose, testId = "modal-close" }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="text-blue-400" size={16}/>}
        <div className="text-white font-semibold" style={{ fontFamily: "Outfit" }}>{title}</div>
      </div>
      {onClose && (
        <button onClick={onClose} data-testid={testId} className="text-slate-400 hover:text-white"><X size={18}/></button>
      )}
    </div>
  );
}
